/**
 * ClipReviewModal — loads the signed, TTL-limited clip URI for an anomaly and plays it,
 * with the linked event + correlated Orees data beside it (CLAUDE.md "Clip review" screen).
 * Handles 404 / expired clips gracefully (NotFoundError).
 */
import { useEffect, useState } from "react";
import type { AnomalyCard, ClipLink } from "@/types/contract";
import { api, NotFoundError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { LinkedData } from "@/components/anomaly/LinkedData";
import { XIcon, ClockIcon, AlertIcon } from "@/components/ui/icons";
import { CATEGORY_LABELS } from "@/lib/anomaly";
import { ttlRemaining } from "@/lib/utils";

interface Props {
  card: AnomalyCard;
  onClose: () => void;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; clip: ClipLink }
  | { kind: "expired" }
  | { kind: "error"; message: string };

export function ClipReviewModal({ card, onClose }: Props) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: "loading" });
    api
      .getClip(card.id, ctrl.signal)
      .then((clip) => {
        if (ttlRemaining(clip.ttl_expires_at) === null) {
          setState({ kind: "expired" });
        } else {
          setState({ kind: "ready", clip });
        }
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof NotFoundError) {
          setState({ kind: "expired" });
        } else {
          setState({
            kind: "error",
            message:
              e instanceof Error ? e.message : "Не удалось получить клип",
          });
        }
      });
    return () => ctrl.abort();
  }, [card.id]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр видеофрагмента"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-card bg-surface p-4 shadow-float sm:rounded-card">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <Badge tone="neutral">{CATEGORY_LABELS[card.category]}</Badge>
              <StatusBadge status={card.status} />
            </div>
            <h2 className="text-lg font-semibold text-ink">{card.title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Закрыть">
            <XIcon size={18} />
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-[1.4fr_1fr]">
          <div className="min-w-0">
            <div className="aspect-video overflow-hidden rounded-control border border-border bg-ink/90">
              {state.kind === "loading" && (
                <div className="skeleton h-full w-full" aria-label="Загрузка клипа" />
              )}
              {state.kind === "ready" && (
                <video
                  className="h-full w-full"
                  src={state.clip.uri}
                  controls
                  autoPlay
                  playsInline
                />
              )}
              {(state.kind === "expired" || state.kind === "error") && (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-primary-foreground">
                  <AlertIcon size={26} />
                  <p className="text-sm">
                    {state.kind === "expired"
                      ? "Клип удалён по сроку хранения (TTL). Доступно только описание события."
                      : state.message}
                  </p>
                </div>
              )}
            </div>

            {state.kind === "ready" && (
              <p className="tabular mt-2 flex items-center gap-1.5 text-xs text-muted">
                <ClockIcon size={13} />
                {ttlRemaining(state.clip.ttl_expires_at) ??
                  "срок хранения истёк"}{" "}
                · доступ к клипу журналируется
              </p>
            )}
          </div>

          <aside className="min-w-0">
            <div className="eyebrow mb-2">Контекст события</div>
            <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <dt className="text-xs text-muted">Событие</dt>
                <dd className="tabular truncate text-sm font-semibold text-ink">
                  {card.event_id}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted">Точка</dt>
                <dd className="tabular truncate text-sm font-semibold text-ink">
                  {card.restaurant_id}
                </dd>
              </div>
            </dl>
            {Object.keys(card.linked_data).length > 0 && (
              <>
                <div className="eyebrow mb-2">Сшитые данные Orees</div>
                <LinkedData data={card.linked_data} />
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
